/**
 * Bảng ngọc: Board → ChainController (luật input) → BoardView (vẽ/anim), cùng layout thích ứng
 * và nút Cast. Board và kết quả thả chuỗi do battle thật (@ml/core, qua `onRelease`) quyết định;
 * scene này chỉ lo input + anim bảng. Cảnh chiến đấu/HUD/thanh skill do BattleView vẽ.
 */
import { Application } from 'pixi.js';
import {
  formatBoard,
  getCell,
  getLinkCandidates,
  hashBoard,
  isCompleteMove,
  NO_PATTERNS,
  type Board,
  type Coord,
  type LinkContext,
} from '@ml/core';
import type { Config } from '@ml/config';
import type { BoardEvent } from './boardEvents.ts';
import { ChainController, type ChainOutcome } from './chain.ts';
import { BoardInput } from './input.ts';
import { cellAt, cellInHitCircle, computeLayout, type Layout } from './layout.ts';
import { BoardView } from './view.ts';
import { readSafeArea, viewportSize } from './viewport.ts';

export type MoveLog = {
  path: Coord[];
  ok: boolean;
  reason?: string;
  cleared: number;
  events: BoardEvent['type'][];
};

export type BoardSceneHooks = {
  /** Thả một chuỗi hoàn chỉnh. Nơi gọi chạy battle rồi gọi `playResult`. */
  onRelease: (path: readonly Coord[]) => void;
  /** Layout vừa tính lại (để BattleView vẽ theo). */
  onLayout?: (l: Layout) => void;
  /** Chuỗi đang chọn đổi (để thanh skill sáng ô khớp). */
  onSelection?: (path: readonly Coord[]) => void;
};

export class BoardScene {
  board: Board;
  readonly chain: ChainController;
  readonly view: BoardView;
  layout!: Layout;
  score = 0;
  turns = 0;
  /** Khoá input (đang diễn pha monster, đang chọn buff, hết trận…). */
  locked = false;
  /** Vẽ theo nhu cầu: chỉ render khi có thay đổi hoặc anim (đỡ tốn pin/CPU khi đứng yên). */
  private dirty = true;
  readonly moves: MoveLog[] = [];
  private readonly castBtn: HTMLButtonElement;
  private readonly boardBox: HTMLDivElement;
  private ctx: LinkContext;

  constructor(
    private readonly app: Application,
    readonly config: Config,
    board: Board,
    ctx: LinkContext,
    private readonly hooks: BoardSceneHooks,
  ) {
    this.board = board;
    this.ctx = ctx;
    this.chain = new ChainController(this.board, config, ctx);
    this.view = new BoardView(
      this.board,
      app.ticker,
      config.ui.anim,
      config.ui.showLinkRange ? config.ui.dimNonCandidatesPct : 0,
    );
    app.stage.addChild(this.view.root);

    this.castBtn = document.createElement('button');
    this.castBtn.id = 'cast';
    this.castBtn.type = 'button';
    this.castBtn.textContent = 'Cast';
    this.castBtn.hidden = true;
    this.castBtn.addEventListener('click', () => this.apply(this.chain.cast()));
    document.body.appendChild(this.castBtn);

    // Hộp DOM trùng khung bảng (không nhận chạm) để test/QA đo bounding box.
    this.boardBox = document.createElement('div');
    this.boardBox.dataset.testid = 'board';
    this.boardBox.className = 'board-box';
    document.body.appendChild(this.boardBox);

    new BoardInput(app.canvas, {
      cellAt: (x, y) => cellAt(this.layout, this.board.width, this.board.height, x, y),
      hitCell: (x, y) =>
        cellInHitCircle(
          this.layout,
          this.board.width,
          this.board.height,
          x,
          y,
          config.link.dragHitRadiusPct,
        ),
      cellSize: () => this.layout.cell,
      enabled: () => !this.view.animating && !this.locked,
      down: (c) => this.apply(this.chain.down(c)),
      enter: (c) => this.apply(this.chain.enter(c)),
      up: () => this.apply(this.chain.up()),
      cancel: () => this.apply(this.chain.cancel()),
    });
    this.relayout();
  }

  relayout(): void {
    const vp = viewportSize();
    this.app.renderer.resize(vp.width, vp.height);
    const l = computeLayout(
      vp.width,
      vp.height,
      readSafeArea(),
      this.config.ui.layout,
      this.board.width,
      this.board.height,
    );
    this.layout = l;
    this.view.setLayout(l);
    this.view.setSelection(this.chain.path, this.chain.candidates);
    Object.assign(this.boardBox.style, {
      left: `${l.board.x}px`,
      top: `${l.board.y}px`,
      width: `${l.board.width}px`,
      height: `${l.board.height}px`,
    });
    const bw = Math.min(120, l.safe.width / 3);
    Object.assign(this.castBtn.style, {
      left: `${l.board.x + l.board.width - bw}px`,
      top: `${l.skillBar.y + 6}px`,
      width: `${bw}px`,
      height: `${Math.max(36, Math.min(44, l.skillBar.height - 12))}px`,
    });
    this.hooks.onLayout?.(l);
    this.updateHud();
  }

  markDirty(): void {
    this.dirty = true;
  }

  needsRender(): boolean {
    const d = this.dirty || this.view.busy;
    this.dirty = false;
    return d;
  }

  private updateHud(): void {
    this.dirty = true;
    this.castBtn.hidden = !this.chain.castable || this.view.animating || this.locked;
  }

  private apply(o: ChainOutcome): void {
    if (o.type === 'release') {
      if (this.locked || this.view.animating) return;
      this.hooks.onRelease(o.path);
    }
    this.view.setSelection(this.chain.path, this.chain.candidates);
    this.hooks.onSelection?.(this.chain.path);
    this.updateHud();
  }

  /** Thả chuỗi bị battle từ chối (chuỗi hỏng/huỷ): không đổi bảng. */
  rejectRelease(path: readonly Coord[], reason: string): void {
    this.moves.push({ path: [...path], ok: false, reason, cleared: 0, events: [] });
  }

  /** Diễn kết quả một nước (bảng cuối của bước battle + sự kiện anim). */
  async playResult(
    path: readonly Coord[],
    next: Board,
    events: readonly BoardEvent[],
    damage: number,
    ctx: LinkContext,
  ): Promise<void> {
    const cleared = events.reduce((n, e) => (e.type === 'pop' ? n + e.cells.length : n), 0);
    this.moves.push({ path: [...path], ok: true, cleared, events: events.map((e) => e.type) });
    this.score += damage;
    this.turns++;
    this.board = next;
    this.ctx = ctx;
    this.chain.setBoard(next, ctx);
    this.updateHud();
    await this.view.play(next, events);
    this.view.setSelection(this.chain.path, this.chain.candidates);
    this.updateHud();
  }

  /** Bảng mới (tầng mới, chơi lại) — không anim. */
  setBoard(board: Board, ctx: LinkContext): void {
    this.board = board;
    this.ctx = ctx;
    this.chain.setBoard(board, ctx);
    this.view.setBoard(board);
    this.view.setSelection([], []);
    this.updateHud();
  }

  setLocked(v: boolean): void {
    this.locked = v;
    if (v) this.chain.setBoard(this.board, this.ctx);
    this.view.setSelection(this.chain.path, this.chain.candidates);
    this.updateHud();
  }

  /** Toạ độ client (CSS px) tâm ô — cho test e2e. */
  cellCenter(x: number, y: number): { x: number; y: number } {
    const rect = this.app.canvas.getBoundingClientRect();
    const c = this.layout.cell;
    return {
      x: rect.left + this.layout.gridX + (x + 0.5) * c,
      y: rect.top + this.layout.gridY + (y + 0.5) * c,
    };
  }

  /** Tìm một nước kéo được dài `len` (DFS theo ứng viên, có lọc tiền tố của hero). */
  findMove(len = this.config.link.minLength): Coord[] | null {
    const ctx = this.ctx ?? NO_PATTERNS;
    const starts = getLinkCandidates(this.board, [], this.config, ctx);
    const dfs = (path: Coord[]): Coord[] | null => {
      if (path.length >= len)
        return isCompleteMove(this.board, path, this.config, ctx) ? path : null;
      for (const c of getLinkCandidates(this.board, path, this.config, ctx)) {
        const r = dfs([...path, c]);
        if (r) return r;
      }
      return null;
    };
    for (const s of starts) {
      const cell = getCell(this.board, s.x, s.y);
      if (!cell || cell.t !== 'orb' || cell.overlay) continue;
      const r = dfs([s]);
      if (r) return r;
    }
    return null;
  }

  debugState(): Record<string, unknown> {
    return {
      ready: true,
      board: formatBoard(this.board),
      boardHash: hashBoard(this.board),
      path: this.chain.path.map((c) => ({ ...c })),
      candidates: this.chain.candidates.map((c) => ({ ...c })),
      castable: this.chain.castable,
      score: this.score,
      turns: this.turns,
      moves: this.moves,
      animating: this.view.animating,
      locked: this.locked,
      layout: this.layout,
    };
  }
}
